import type {
  AddToShelfInput,
  Book,
  BookGenre,
  Paginated,
  ShelfStatus,
} from '@booknest/shared';

export interface FeaturedBookResponse {
  book: Book | null;
  weekKey: string;
}
export interface DiscoverShelf {
  genre: BookGenre;
  books: Book[];
}
export interface DiscoverShelvesResponse {
  shelves: DiscoverShelf[];
}

import { apiClient } from '@/shared/api/client.js';

export interface ShelfEntryView {
  id: string;
  userId: string;
  bookId: string;
  status: ShelfStatus;
  currentPage: number | null;
  rating: number | null;
  notes: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  book: Book;
}

export interface SearchBooksResponse extends Paginated<Book> {}
export interface ListShelfResponse {
  items: ShelfEntryView[];
}
export interface AddToShelfResponse {
  entry: ShelfEntryView;
}
export interface UpdateShelfEntryResponse {
  entry: ShelfEntryView;
}
export interface UpcomingReleasesResponse {
  items: ShelfEntryView[];
}
export interface TrendingSourceInfo {
  source: string;
  rank: number;
  weight: number;
  context: string | null;
}
export interface TrendingBook extends Book {
  sources: TrendingSourceInfo[];
}
export interface TrendingBooksResponse {
  items: TrendingBook[];
}
export interface ReadingByMonthResponse {
  items: { month: string; count: number }[];
}
export interface PagesThisWeekResponse {
  pages: number;
  booksContributing: number;
}
export interface UpcomingFromAuthorItem {
  book: Book;
  author: string;
}
export interface UpcomingFromAuthorsResponse {
  items: UpcomingFromAuthorItem[];
}
export interface AuthorSuggestion {
  author: string;
  bookCount: number;
}
export interface AuthorSuggestionsResponse {
  items: AuthorSuggestion[];
}
export interface ReadingActivityCell {
  date: string;
  level: 0 | 1 | 2 | 3 | 4;
}
export interface ReadingActivityResponse {
  cells: ReadingActivityCell[];
  activeDays: number;
}

export const libraryApi = {
  async searchBooks(
    q: string,
    limit = 20,
    offset = 0,
    genre?: BookGenre,
  ): Promise<SearchBooksResponse> {
    const { data } = await apiClient.get<SearchBooksResponse>('/books/search', {
      params: { q, limit, offset, ...(genre && { genre }) },
    });
    return data;
  },

  async addToShelf(input: AddToShelfInput): Promise<AddToShelfResponse> {
    const { data } = await apiClient.post<AddToShelfResponse>('/shelf', input);
    return data;
  },

  async listMyShelf(status?: ShelfStatus): Promise<ListShelfResponse> {
    const { data } = await apiClient.get<ListShelfResponse>('/shelf', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async updateEntry(
    entryId: string,
    patch: { status?: ShelfStatus; rating?: number | null },
  ): Promise<UpdateShelfEntryResponse> {
    const { data } = await apiClient.patch<UpdateShelfEntryResponse>(
      `/shelf/${entryId}`,
      patch,
    );
    return data;
  },

  async removeFromShelf(entryId: string): Promise<void> {
    await apiClient.delete(`/shelf/${entryId}`);
  },

  async listUpcomingReleases(limit = 5): Promise<UpcomingReleasesResponse> {
    const { data } = await apiClient.get<UpcomingReleasesResponse>(
      '/releases/upcoming',
      { params: { limit } },
    );
    return data;
  },

  async getFeaturedBook(): Promise<FeaturedBookResponse> {
    const { data } = await apiClient.get<FeaturedBookResponse>('/books/featured');
    return data;
  },

  async getDiscoverShelves(): Promise<DiscoverShelvesResponse> {
    const { data } = await apiClient.get<DiscoverShelvesResponse>(
      '/books/discover-shelves',
    );
    return data;
  },

  async listTrendingBooks(limit = 5): Promise<TrendingBooksResponse> {
    const { data } = await apiClient.get<TrendingBooksResponse>(
      '/books/trending',
      { params: { limit } },
    );
    return data;
  },

  async getReadingByMonth(): Promise<ReadingByMonthResponse> {
    const { data } = await apiClient.get<ReadingByMonthResponse>(
      '/profile/reading-by-month',
    );
    return data;
  },

  async getPagesThisWeek(): Promise<PagesThisWeekResponse> {
    const { data } = await apiClient.get<PagesThisWeekResponse>(
      '/profile/pages-this-week',
    );
    return data;
  },

  async getUpcomingFromFavoriteAuthors(
    limit = 6,
  ): Promise<UpcomingFromAuthorsResponse> {
    const { data } = await apiClient.get<UpcomingFromAuthorsResponse>(
      '/releases/from-favorite-authors',
      { params: { limit } },
    );
    return data;
  },

  async suggestAuthors(
    q: string,
    limit = 8,
  ): Promise<AuthorSuggestionsResponse> {
    const { data } = await apiClient.get<AuthorSuggestionsResponse>(
      '/authors/suggest',
      { params: { q, limit } },
    );
    return data;
  },

  async getReadingActivity(): Promise<ReadingActivityResponse> {
    const { data } = await apiClient.get<ReadingActivityResponse>(
      '/profile/reading-activity',
    );
    return data;
  },
};
